import styled from 'styled-components';
import THEMES from '~/themes/themes';

interface SelectButtonProps {
  isFocused: boolean;
  disabled?: boolean;
}

interface OptionItemProps {
  isSelected: boolean;
}

interface IconProps {
  isFocused: boolean;
}

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  margin-bottom: 10px;

  label {
    font-size: 12px;
    color: black;
    font-weight: 500;
    display: block;
  }
`;

export const SelectButton = styled.div<SelectButtonProps>`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background-color: transparent;
  border: 1px solid #e71037;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  gap: 8px;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  border-color: #e71037;
  border-width: 0px 0px 1px 0px;

  &:hover:not(:disabled) {
    border-color: #e71037;
  }
`;

export const SelectContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 24px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  span {
    color: #4a4b4a;
    font-size: 14px;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const Placeholder = styled.span`
  color: #999;
  font-size: 14px;
`;

export const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

export const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: #e71037;
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background-color: #d00930;
  }
`;

export const ChipRemoveButton = styled.span`
  cursor: pointer;
  font-weight: bold;
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.8;
  }
`;

export const ClearButton = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 18px;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    color: #666;
  }
`;

export const IconRight = styled.img<IconProps>`
  width: 20px;
  height: 20px;
  cursor: pointer;
  flex-shrink: 0;
  transform: ${({ isFocused }) => (isFocused ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.3s ease;
`;

export const DropdownContainer = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  min-width: 100%;
`;

export const SearchInput = styled.input`
  padding: 10px 12px;
  border: none;
  border-bottom: 1px solid #e0e0e0;
  border-radius: 4px 4px 0 0;
  font-size: 14px;
  outline: none;
  background-color: #f9f9f9;

  &:focus {
    background-color: #fff;
    border-bottom-color: #e71037;
  }

  &::placeholder {
    color: #999;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

export const OptionsList = styled.div`
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  max-height: 250px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;

    &:hover {
      background: #999;
    }
  }
`;

export const OptionItem = styled.div<OptionItemProps>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background-color: ${({ isSelected }) =>
    isSelected ? '#f5f5f5' : 'transparent'};
  border-left: 3px solid ${({ isSelected }) => (isSelected ? '#e71037' : 'transparent')};

  &:hover {
    background-color: #f9f9f9;
  }

  span {
    flex: 1;
    color: #4a4b4a;
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const NoResults = styled.div`
  padding: 16px;
  text-align: center;
  color: #999;
  font-size: 14px;
`;

export const ErrorMessage = styled.span`
  color: ${({ theme }) => theme.colors.error || '#d32f2f'};
  font-size: 12px;
  margin-top: 4px;
  display: block;
`;

export const CheckBox = styled.input.attrs({ type: 'checkbox' })`
  appearance: none;
  background-color: #fff;
  margin: 0;
  font: inherit;
  color: #a1a1a1;
  width: 18px;
  height: 18px;
  border: 1.5px solid #a1a1a1;
  border-radius: 3px;
  cursor: pointer;
  display: grid;
  place-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    border-color: #e71037;
  }

  &:before {
    content: '';
    width: 10px;
    height: 10px;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    box-shadow: inset 1em 1em #e71037;
    transform-origin: center center;
    clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
  }

  &:checked {
    border-color: #e71037;
    background-color: #fff;
  }

  &:checked:before {
    transform: scale(1);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;
